import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { getScenarios } from "../lib/api";
import { useSector } from "../lib/sector";
import { useBrand } from "../lib/brand";
import { KatlasNodeBadge } from "../components/KatlasProofCard";
import { ActiveSectorPill, SectorDisclaimer } from "../components/SectorPicker";
import CarExplainer from "../components/CarExplainer";
import {
    ArrowUpRight,
    ShieldCheck,
    Network,
    Leaf,
    Lock,
    Scale,
    GitBranch,
    FileCheck,
    Wallet,
    GraduationCap,
    Linkedin,
} from "lucide-react";

// Build-time brand check. CRA's DefinePlugin inlines this; Terser then DCEs
// the unreachable branch, removing all RBT-only string literals from the
// cierge bundle.

// PORTFOLIO data is gated at build time so RBT-only names (people, partner)
// never appear in the cierge bundle.
const PORTFOLIO = process.env.REACT_APP_BRAND_MODE === "cierge"
    ? []
    : [
        {
            key: "mijal",
            monogram: "MR",
            name: "Mijal Raja",
            title: "Chief Executive Officer, Right Brain Thinking International",
            role: "Executive education, customer engagement and responsible technology leadership.",
            bio: "Mijal leads Right Brain Thinking International's renewed focus on technology and sustainability leadership. His role in the Responsible Technology Leadership Lab is to connect executive education, organisational transformation and customer-facing adoption pathways.",
            contributions: [
                "Customer and executive engagement",
                "Leadership education framing",
                "Responsible technology adoption pathway",
                "GCC / international relationship development",
                "Translation of customer priorities into workshop and lab scenarios",
            ],
            partnerType: "rbt",
        },
        {
            key: "kamal",
            monogram: "KB",
            name: "Professor Kamal Bechkoum",
            title: "Programme Director / AI and Cybersecurity Lead, Right Brain Thinking International",
            role: "Academic and technical authority for AI, cybersecurity and responsible adoption.",
            bio: "Professor Kamal Bechkoum brings deep experience in artificial intelligence, cybersecurity, digital innovation and higher education leadership. His role in the Responsible Technology Leadership Lab is to provide the AI/cyber education backbone and help customers understand the risks, controls and leadership responsibilities involved in adoption.",
            contributions: [
                "AI and cybersecurity education",
                "Secure-by-design technology adoption",
                "Executive and board-level AI/cyber training",
                "Risk-aware AI implementation",
                "Academic and professional credibility",
            ],
            partnerType: "rbt",
        },
        {
            key: "katlas",
            monogram: "KATLAS",
            name: "KATLAS Technology",
            title: "Operational Governance Partner",
            role: "Live governance substrate for custody, authority and receipts.",
            bio: "KATLAS provides the operational governance layer beneath the sandbox. It enables customer teams to rehearse responsible AI adoption, cybersecure collaboration and sustainable technology governance through role-based authority, controlled evidence exchange and verifiable receipts.",
            contributions: [
                "CAR model: Custody, Authority, Receipts",
                "Human authority gates",
                "Governed AI-assisted workflows",
                "Evidence packs and receipt verification",
                "Live KATLAS node/API integration where available",
                "Minimal metadata and evidence-reference payloads only",
            ],
            partnerType: "katlas",
        },
    ];

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/d3e67806-f6bb-42a4-9218-e16443524b47/images/5e22e69dc3be821ba63249ce2aff1d8e6ca01ac652252298848cfebc11dc7bcf.png";

const SCENARIO_IMGS = {
    "responsible-ai-adoption":
        "https://static.prod-images.emergentagent.com/jobs/d3e67806-f6bb-42a4-9218-e16443524b47/images/d9c22ad0add9f694c75194ea372807ab4fa05878701f08c29b7d3bd1a56c6a33.png",
    "cybersecure-ecosystem":
        "https://images.unsplash.com/photo-1644088379091-d574269d422f?auto=format&fit=crop&w=1200&q=70",
    "sustainable-technology-governance":
        "https://images.unsplash.com/photo-1556983852-43bf21186b2a?auto=format&fit=crop&w=1200&q=70",
};

const TENETS = [
    {
        n: "01",
        icon: ShieldCheck,
        title: "Responsible use of technology",
        body: "Technology should support better human judgement and organisational coordination. AI may assist — but it must not quietly take over accountable decisions.",
        span: "md:col-span-2",
    },
    {
        n: "02",
        icon: Lock,
        title: "Cybersecure by design",
        body: "Cybersecurity is an operating principle across roles, data flows, decisions and handoffs — not a defensive perimeter bolted on at the edge.",
    },
    {
        n: "03",
        icon: Network,
        title: "Efficient ecosystems",
        body: "Governed technology should reduce duplication, unnecessary data movement, fragmented reporting and slow administrative coordination.",
    },
    {
        n: "04",
        icon: Wallet,
        title: "Incentivised participation",
        body: "Each actor should see what they gain, what they are responsible for, and how the system discourages unsafe or extractive behaviour.",
    },
    {
        n: "05",
        icon: Leaf,
        title: "Long-term equilibrium",
        body: "Resilient, sustainable operating models — not just optimisation for speed, cost or automation.",
        span: "md:col-span-2",
    },
    {
        n: "06",
        icon: FileCheck,
        title: "Evidence-backed accountability",
        body: "Every governed action produces a receipt: custody, authority, decision, evidence shared, evidence withheld, timestamp, verification.",
    },
    {
        n: "07",
        icon: GraduationCap,
        title: "Education through rehearsal",
        body: "A leadership rehearsal environment. Customers experience the consequences of good — and poor — governance choices.",
    },
];

export default function Landing() {
    const [data, setData] = useState(null);
    const { sectorId } = useSector();
    const brand = useBrand();
    const isCierge = brand.mode === "cierge";
    useEffect(() => {
        if (isCierge) {
            setData({ scenarios: [] });
            return;
        }
        getScenarios(sectorId).then(setData).catch(() => setData({ scenarios: [] }));
    }, [sectorId, isCierge]);

    return (
        <div data-testid="landing-page">
            {/* HERO */}
            <section className="relative border-b border-slate-200 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMG}
                        alt=""
                        className="w-full h-full object-cover opacity-[0.18]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/70 to-white" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-20">
                    <div className="flex items-center gap-3 mb-6 flex-wrap">
                        <span className="overline">{brand.wordmark} · Sandbox v1</span>
                        <span className="hidden md:inline w-8 h-px bg-slate-300" />
                        <KatlasNodeBadge />
                        {!isCierge && (
                            <>
                                <span className="hidden md:inline w-8 h-px bg-slate-300" />
                                <ActiveSectorPill />
                            </>
                        )}
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 max-w-4xl leading-[1.05]">
                        Responsible Technology
                        <br />
                        <span className="text-slate-500">Leadership Lab.</span>
                    </h1>
                    <p className="mt-6 text-lg lg:text-xl text-slate-700 max-w-3xl leading-relaxed">
                        {brand.positioning}
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-3">
                        {isCierge ? (
                            <Link
                                to="/responsible-technology"
                                className="btn-primary text-sm tracking-tight px-5 py-3 rounded-sm inline-flex items-center gap-2"
                                data-testid="hero-governed-action-btn"
                            >
                                {brand.primary_cta_label}{" "}
                                <ArrowUpRight size={16} strokeWidth={1.5} />
                            </Link>
                        ) : (
                            <a
                                href="#scenarios"
                                className="btn-primary text-sm tracking-tight px-5 py-3 rounded-sm inline-flex items-center gap-2"
                                data-testid="hero-explore-scenarios-btn"
                            >
                                Explore scenario labs <ArrowUpRight size={16} strokeWidth={1.5} />
                            </a>
                        )}
                        {brand.show_tech_lab && (
                            <Link
                                to="/tech-lab"
                                className="btn-ghost text-sm tracking-tight px-5 py-3 rounded-sm"
                                data-testid="hero-tech-lab-btn"
                            >
                                See the customer pathway
                            </Link>
                        )}
                    </div>
                    {!isCierge && (
                        <div className="mt-6">
                            <SectorDisclaimer />
                        </div>
                    )}
                </div>
            </section>

            {/* CAR EXPLAINER */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
                <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
                    <div>
                        <div className="overline">Governance substrate</div>
                        <h2 className="text-2xl lg:text-4xl font-bold tracking-tight">
                            KATLAS is CAR-first.
                        </h2>
                    </div>
                    <p className="max-w-md text-sm text-slate-600 leading-relaxed">
                        Not a dashboard. Not a tamperproof log. Runtime operational
                        governance: who has custody, who has authority, what the receipts
                        prove.
                    </p>
                </div>
                <CarExplainer />
            </section>

            {/* TENETS */}
            {brand.show_rbt_tenets && process.env.REACT_APP_BRAND_MODE !== "cierge" && (
            <section className="border-y border-slate-200 bg-slate-50/60">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
                        <div>
                            <div className="overline">RBT Baseline Tenets</div>
                            <h2 className="text-2xl lg:text-4xl font-bold tracking-tight">
                                Seven non-negotiables.
                            </h2>
                        </div>
                        <p className="max-w-md text-sm text-slate-600 leading-relaxed">
                            Every scenario in this lab is built around these tenets. They
                            structure both the education and the operational governance below
                            it.
                        </p>
                    </div>
                    <div
                        className="grid grid-cols-1 md:grid-cols-3 gap-4"
                        data-testid="tenets-grid"
                    >
                        {TENETS.map((t) => (
                            <article
                                key={t.n}
                                className={`tile rounded-sm p-6 ${t.span || ""}`}
                                data-testid={`tenet-${t.n}`}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <span className="font-mono text-[11px] text-slate-500 tracking-[0.22em]">
                                        TENET {t.n}
                                    </span>
                                    <t.icon size={18} strokeWidth={1.5} className="text-slate-700" />
                                </div>
                                <h3 className="text-lg font-bold tracking-tight mb-2">
                                    {t.title}
                                </h3>
                                <p className="text-sm text-slate-700 leading-relaxed">{t.body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* RIGHT BRAIN PORTFOLIO */}
            {brand.show_portfolio && process.env.REACT_APP_BRAND_MODE !== "cierge" && (
            <section
                className="border-t border-slate-200"
                data-testid="portfolio-section"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
                        <div>
                            <div className="overline">Right Brain Portfolio</div>
                            <h2 className="text-2xl lg:text-4xl font-bold tracking-tight max-w-3xl">
                                The people and partner behind the lab.
                            </h2>
                            <p className="mt-3 text-sm text-slate-600 max-w-2xl leading-relaxed">
                                Education, AI/cyber expertise and operational governance brought
                                together for responsible technology adoption.
                            </p>
                        </div>
                    </div>

                    <div
                        className="grid grid-cols-1 lg:grid-cols-3 gap-5"
                        data-testid="portfolio-grid"
                    >
                        {PORTFOLIO.map((p) => (
                            <PortfolioCard key={p.key} profile={p} />
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* GOVERNED ACTION (Cierge mode) */}
            {isCierge && (
                <section
                    className="border-t border-slate-200"
                    data-testid="cierge-governed-action-section"
                >
                    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
                        <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
                            <div>
                                <div className="overline">Primary governed action</div>
                                <h2 className="text-2xl lg:text-4xl font-bold tracking-tight max-w-3xl">
                                    Approve or refuse a responsible technology recommendation.
                                </h2>
                                <p className="mt-3 text-sm text-slate-600 max-w-2xl leading-relaxed">
                                    AI suggests. Authorised roles decide. KATLAS signs the receipt.
                                </p>
                            </div>
                            <Link
                                to="/responsible-technology"
                                className="btn-primary text-sm tracking-tight px-5 py-3 rounded-sm inline-flex items-center gap-2"
                                data-testid="landing-governed-action-cta"
                            >
                                Run the governed action <ArrowUpRight size={16} strokeWidth={1.5} />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {[
                                "Custody controls the evidence. Authority governs the action. Receipts prove what happened.",
                                "KATLAS records the governed action — not a copy of everyone's private data. Evidence stays at source.",
                                "AI suggests. Authorised roles decide. KATLAS signs the receipt.",
                            ].map((l, i) => (
                                <div key={l} className="tile rounded-sm p-5" data-testid={`cierge-baseline-line-${i}`}>
                                    <p className="text-sm text-slate-800 leading-relaxed">{l}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* SCENARIOS */}
            {brand.show_scenario_cards && (
            <section
                id="scenarios"
                className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20"
            >
                <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <div className="overline">Three rehearsal pathways</div>
                        <h2 className="text-2xl lg:text-4xl font-bold tracking-tight">
                            Choose a scenario.
                        </h2>
                    </div>
                    <p className="max-w-md text-sm text-slate-600 leading-relaxed">
                        One reusable sandbox. Three pathways. Each ends with an accountable
                        human decision and a KATLAS-signed receipt.
                    </p>
                </div>

                <div
                    className="grid grid-cols-1 lg:grid-cols-3 gap-5"
                    data-testid="scenario-cards"
                >
                    {(data?.scenarios || []).map((s, idx) => (
                        <ScenarioCard
                            key={s.slug}
                            scenario={s}
                            image={SCENARIO_IMGS[s.slug]}
                            index={idx + 1}
                        />
                    ))}
                </div>
            </section>
            )}
        </div>
    );
}

function ScenarioCard({ scenario, image, index }) {
    return (
        <article
            className="tile rounded-sm overflow-hidden flex flex-col"
            data-testid={`scenario-card-${scenario.slug}`}
        >
            <div className="relative h-44 bg-slate-100 overflow-hidden">
                {image && (
                    <img
                        src={image}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.22em] uppercase bg-white/95 text-slate-900 px-2 py-1">
                    Lab 0{index}
                </span>
            </div>
            <div className="p-6 flex flex-col flex-1">
                <h3 className="text-xl font-bold tracking-tight mb-2">{scenario.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                    {scenario.tagline}
                </p>

                <div className="mt-2 mb-4">
                    <div className="overline mb-2">Roles involved</div>
                    <div className="flex flex-wrap gap-1.5">
                        {scenario.roles.map((r) => (
                            <span
                                key={r}
                                className="text-[11px] font-mono text-slate-700 border border-slate-200 px-2 py-0.5"
                            >
                                {r}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="mt-auto pt-4 border-t border-slate-200 space-y-3">
                    <div>
                        <div className="overline mb-1">What KATLAS proves</div>
                        <p className="text-sm text-slate-700 leading-relaxed">
                            {scenario.what_katlas_proves}
                        </p>
                    </div>
                    <Link
                        to={`/scenario/${scenario.slug}`}
                        className="btn-primary text-sm tracking-tight px-4 py-2.5 rounded-sm inline-flex items-center gap-2 self-start"
                        data-testid={`start-scenario-${scenario.slug}`}
                    >
                        Start scenario <ArrowUpRight size={14} strokeWidth={1.5} />
                    </Link>
                </div>
            </div>
        </article>
    );
}

// PortfolioCard contains the literal "RBT Portfolio" badge text. The whole
// function body is gated at build time so cierge builds DCE the RBT strings.
const PortfolioCard = process.env.REACT_APP_BRAND_MODE === "cierge"
    ? function PortfolioCardStub() { return null; }
    : function PortfolioCard({ profile }) {
    const isKatlas = profile.partnerType === "katlas";
    return (
        <article
            className="tile rounded-sm overflow-hidden flex flex-col"
            data-testid={`portfolio-card-${profile.key}`}
        >
            {/* Header: monogram tile or approved headshot */}
            <div
                className={`relative h-40 flex items-center justify-center overflow-hidden ${
                    isKatlas ? "bg-slate-900" : "bg-slate-100"
                }`}
            >
                {profile.image ? (
                    <img
                        src={profile.image}
                        alt={profile.name}
                        className="w-full h-full object-cover"
                    />
                ) : isKatlas ? (
                    <span className="font-mono tracking-[0.22em] text-slate-100 text-xl">
                        {profile.monogram}
                    </span>
                ) : (
                    <span className="font-bold tracking-tight text-slate-900 text-5xl">
                        {profile.monogram}
                    </span>
                )}
                <span
                    className={`absolute top-3 left-3 font-mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 ${
                        isKatlas
                            ? "bg-slate-100/95 text-slate-900"
                            : "bg-white/95 text-slate-900"
                    }`}
                >
                    {isKatlas ? "Governance Partner" : "RBT Portfolio"}
                </span>
            </div>

            <div className="p-6 flex flex-col flex-1">
                <h3
                    className="text-xl font-bold tracking-tight"
                    data-testid={`portfolio-name-${profile.key}`}
                >
                    {profile.name}
                </h3>
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mt-1">
                    {profile.title}
                </p>

                <p className="text-sm text-slate-700 leading-relaxed mt-4">
                    {profile.bio}
                </p>

                <div className="mt-5 pt-4 border-t border-slate-200">
                    <div className="overline mb-2">Role in the lab</div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                        {profile.role}
                    </p>
                </div>

                <div className="mt-5">
                    <div className="overline mb-2">Contribution to the sandbox</div>
                    <ul className="space-y-1.5 text-sm text-slate-700">
                        {profile.contributions.map((c, i) => (
                            <li key={i} className="flex gap-2">
                                <span className="text-slate-400">·</span>
                                <span>{c}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {profile.linkedin && (
                    <a
                        href={profile.linkedin}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-ghost mt-6 self-start text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2"
                        data-testid={`portfolio-linkedin-${profile.key}`}
                    >
                        <Linkedin size={14} strokeWidth={1.5} /> LinkedIn
                    </a>
                )}
            </div>
        </article>
    );
};

